import { useDebouncedCallback } from '@mantine/hooks'
import { AnimatePresence, motion } from 'motion/react'
import type { CSSProperties } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Input, SearchField } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'
import IcBaselineClose from '~icons/ic/baseline-close'
import IcBaselineNorthWest from '~icons/ic/baseline-north-west'
import IcBaselineSearch from '~icons/ic/baseline-search'
import IconParkNewspaperFolding from '~icons/icon-park-outline/newspaper-folding'
import { createTagPageUrl } from 'src/components/Tag/utils'
import Text from 'src/components/ui/Text'
import { useSearchBarAutocompleteExperiment } from 'src/experiments/experiments/2605/searchBarAutocomplete'
import { useSearchHistory } from 'src/hooks/useSearchHistory'
import { useMyInfo } from 'src/services/apollo/auth'
import type { NormalizedTagItem } from 'src/services/apollo/tack'
import { useTackAutocomplete } from 'src/services/orpc/tack'
import { useTrendingTacks } from 'src/services/orpc/trendingTack'
import { usePublicDynamicConfig } from 'src/services/restful/dynamicConfig'
import { getLocale } from 'src/stores/locale'
import { cx } from 'src/utils/cx'
import { formatQs, useQs } from 'src/utils/qs'
import { useLocalePath, useLocation } from 'src/utils/router/route'
import { track } from 'src/utils/tracker'
import { isExternalUrl } from 'src/utils/url'

function formatCount(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`
  return String(count)
}

type FeaturedItem = {
  title: string
  description: string
  href: string
  cover?: string
}

type SearchDropdownConfig = {
  featured?: {
    [key: string]: FeaturedItem[]
  }
}

function useSearchBar(options: {
  scene: string
  replace?: boolean
  resetOnLeaveSearch?: boolean
}) {
  const [{ q }] = useQs()
  const [keyword, setKeyword] = useState(q || '')
  const [debouncedKeyword, setDebouncedKeyword] = useState(keyword)
  const [isFocused, setIsFocused] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const { data: me } = useMyInfo()
  const { history, addToHistory, removeFromHistory, clearHistory } =
    useSearchHistory()
  const location = useLocation()

  const debouncedSetKeyword = useDebouncedCallback(
    (v: string) => setDebouncedKeyword(v),
    200,
  )

  const handleKeywordChange = (v: string) => {
    setKeyword(v)
    debouncedSetKeyword(v)
  }

  const handleSubmit = (value: string) => {
    const qs = {
      q: value,
      showSignUpModal: me ? undefined : '1',
    }
    addToHistory(value)
    navigate(`/search?${formatQs(qs)}`, { replace: options.replace })
    setIsFocused(false)
    track('search', { scene: options.scene, value })
  }

  const onFillback = (tag: NormalizedTagItem) => {
    const next = tag.displayName ?? tag.name
    debouncedSetKeyword.cancel()
    track('search_dropdown_autocomplete_fill', {
      scene: options.scene,
      keyword,
      codeName: tag.codeName ?? tag.name,
    })
    setKeyword(next)
    setDebouncedKeyword(next)
    inputRef.current?.focus()
  }

  // After leaving search page, reset the input
  useEffect(() => {
    if (options.resetOnLeaveSearch && location.pathname !== '/search') {
      debouncedSetKeyword.cancel()
      setKeyword('')
      setDebouncedKeyword('')
    }
  }, [location.pathname, options.resetOnLeaveSearch, debouncedSetKeyword])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        !containerRef.current?.contains(event.target as Node) &&
        !dropdownRef.current?.contains(event.target as Node)
      ) {
        setIsFocused(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [containerRef, dropdownRef])

  const onHistoryClick = (query: string) => {
    handleSubmit(query)
  }

  return {
    q,
    keyword,
    debouncedKeyword,
    setKeyword: handleKeywordChange,
    isFocused,
    setIsFocused,
    containerRef,
    dropdownRef,
    inputRef,
    handleSubmit,
    history,
    removeFromHistory,
    clearHistory,
    onHistoryClick,
    onFillback,
  }
}

function SearchBarDropdown({
  dropdownRef,
  isFocused,
  history,
  removeFromHistory,
  clearHistory,
  onHistoryClick,
  className,
  disableTopPadding,
  debouncedKeyword,
  enableAutocomplete,
  onFillback,
  scene,
}: {
  dropdownRef: React.RefObject<HTMLDivElement | null>
  onHistoryClick: (query: string) => void
  isFocused: boolean
  history: string[]
  removeFromHistory: (query: string) => void
  clearHistory: () => void
  className?: string
  disableTopPadding?: boolean
  debouncedKeyword: string
  enableAutocomplete: boolean
  onFillback: (tag: NormalizedTagItem) => void
  scene: string
}) {
  return (
    <AnimatePresence>
      {isFocused && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.15 }}
          ref={dropdownRef}
          className={cx(
            'absolute top-full left-0 rounded-b-xl overflow-y-auto bg-background-secondary shadow-xl border border-black/10 dark:border-white/10',
            !disableTopPadding && 'mt-2 rounded-t-xl',
            className,
          )}
        >
          <SearchBarDropdownBody
            history={history}
            onRemoveHistory={removeFromHistory}
            onClearHistory={clearHistory}
            onHistoryClick={onHistoryClick}
            debouncedKeyword={debouncedKeyword}
            enableAutocomplete={enableAutocomplete}
            onFillback={onFillback}
            scene={scene}
          />
        </motion.div>
      )}
    </AnimatePresence>
  )
}

// Designed to be used on desktop
export default function SearchBar(props: {
  className?: string
  style?: CSSProperties
}) {
  const { t } = useTranslation(['common'])
  const { enableAutocomplete } = useSearchBarAutocompleteExperiment()
  const scene = 'home'
  const {
    keyword,
    debouncedKeyword,
    setKeyword,
    isFocused,
    setIsFocused,
    containerRef,
    dropdownRef,
    inputRef,
    handleSubmit,
    history,
    removeFromHistory,
    clearHistory,
    onHistoryClick,
    onFillback,
  } = useSearchBar({ scene, resetOnLeaveSearch: true })

  const isExpanded = isFocused || keyword.length > 0

  return (
    <div ref={containerRef} className="relative" style={props.style}>
      <SearchField
        value={keyword}
        onChange={setKeyword}
        onSubmit={handleSubmit}
        onFocus={() => setIsFocused(true)}
        aria-label={t('common:search')}
        className={cx(
          'group flex items-center h-9 bg-background-secondary rounded-full border border-black/50 dark:border-white/50 transition-all duration-200 ease-in-out',
          isExpanded ? 'w-[412px]' : 'w-[145px]',
          props.className,
        )}
      >
        <IcBaselineSearch className="text-primary size-6 ml-3 shrink-0" />
        <Input
          placeholder={t('common:search')}
          enterKeyHint="search"
          className="flex-1 h-full px-2 bg-transparent outline-none text-primary placeholder:text-primary/50 min-w-0"
          ref={inputRef}
        />
      </SearchField>
      <SearchBarDropdown
        dropdownRef={dropdownRef}
        isFocused={isFocused}
        history={history}
        removeFromHistory={removeFromHistory}
        clearHistory={clearHistory}
        onHistoryClick={onHistoryClick}
        debouncedKeyword={debouncedKeyword}
        enableAutocomplete={enableAutocomplete}
        onFillback={onFillback}
        scene={scene}
        className="w-[412px] max-h-[calc(100vh-100px)]"
      />
    </div>
  )
}

export function MobileSearchBar(props: { className?: string }) {
  const { t } = useTranslation(['common', 'action'])
  const { enableAutocomplete } = useSearchBarAutocompleteExperiment()
  const scene = 'mobile'
  const {
    q,
    keyword,
    debouncedKeyword,
    setKeyword,
    isFocused,
    setIsFocused,
    containerRef,
    dropdownRef,
    inputRef,
    handleSubmit,
    history,
    removeFromHistory,
    clearHistory,
    onHistoryClick,
    onFillback,
  } = useSearchBar({ scene, replace: true })

  return (
    <div ref={containerRef} className="relative w-full">
      <AnimatePresence>
        {isFocused && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-x-0 bottom-0 bg-black/50 z-40"
            style={{
              top: containerRef.current?.getBoundingClientRect().bottom ?? 0,
            }}
            onClick={() => setIsFocused(false)}
            aria-hidden="true"
          />
        )}
      </AnimatePresence>
      <SearchField
        value={keyword}
        onChange={setKeyword}
        onSubmit={handleSubmit}
        onFocus={() => setIsFocused(true)}
        aria-label={t('common:home.search-placeholder')}
        autoFocus={!q}
        className={cx(
          'group flex items-center w-full h-12 bg-[var(--secondary)] rounded-2xl',
          props.className,
        )}
      >
        <IcBaselineSearch className="size-6 ml-3 shrink-0" />
        <Input
          placeholder={t('common:home.search-placeholder')}
          enterKeyHint="search"
          className="flex-1 h-full px-3 py-3 bg-transparent outline-none text-primary placeholder:text-primary/50"
          ref={inputRef}
        />
      </SearchField>
      <SearchBarDropdown
        dropdownRef={dropdownRef}
        isFocused={isFocused}
        history={history}
        removeFromHistory={removeFromHistory}
        clearHistory={clearHistory}
        onHistoryClick={onHistoryClick}
        debouncedKeyword={debouncedKeyword}
        enableAutocomplete={enableAutocomplete}
        onFillback={onFillback}
        scene={scene}
        className="w-full max-h-[60vh] z-50"
        disableTopPadding
      />
    </div>
  )
}

function SearchBarDropdownBody(
  props: DropdownBodyProps & {
    debouncedKeyword: string
    enableAutocomplete: boolean
    onFillback: (tag: NormalizedTagItem) => void
    scene: string
  },
) {
  const trimmed = props.debouncedKeyword.trim()
  const showAutocomplete = props.enableAutocomplete && trimmed.length > 0

  return (
    <>
      {/* Default body stays mounted so its react-query caches survive
          when the user toggles between empty/non-empty input. */}
      <SearchBarDefaultBody
        history={props.history}
        onRemoveHistory={props.onRemoveHistory}
        onClearHistory={props.onClearHistory}
        onHistoryClick={props.onHistoryClick}
        hidden={showAutocomplete}
      />
      {showAutocomplete && (
        <SearchBarAutocompleteList
          keyword={trimmed}
          onFillback={props.onFillback}
          scene={props.scene}
        />
      )}
    </>
  )
}

function SearchBarDefaultBody(props: DropdownBodyProps & { hidden: boolean }) {
  const { data: searchDropdownConfig } =
    usePublicDynamicConfig<SearchDropdownConfig>('searchDropdown')
  // `useTrendingTacks` already passes the user's `lang` to the server, so
  // `tack.displayName` arrives pre-localized — no client-side fallback needed.
  const { data: trendingTacks } = useTrendingTacks(1, 10, 'daily')

  const featured = useMemo(() => {
    const locale = getLocale()
    return Object.keys(searchDropdownConfig?.featured ?? {}).includes(locale)
      ? searchDropdownConfig?.featured?.[locale]
      : searchDropdownConfig?.featured?.en
  }, [searchDropdownConfig?.featured])

  return (
    <div className={cx('flex flex-col gap-4 p-4', props.hidden && 'hidden')}>
      <SearchHistorySection {...props} />

      {trendingTacks && trendingTacks.data.length > 0 && (
        <section>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-primary/60 uppercase tracking-wider mb-2">
            <Text i18nKey="common:search-dropdown.tags" />
          </h3>
          <div className="flex flex-wrap gap-1.5">
            {trendingTacks.data.map(item => {
              const tack = item.tack
              const name = tack.displayName
              return (
                <a
                  key={item.tackId}
                  href={createTagPageUrl(
                    tack.category === 'market' ? 'model' : 'artwork',
                    tack.codeName,
                  )}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs text-primary/70 bg-neutral-200/60 dark:bg-neutral-700/60 hover:bg-neutral-300/60 dark:hover:bg-neutral-600/60 hover:text-primary transition-colors"
                  onClick={() =>
                    track('search_dropdown_trending_tag_click', {
                      tag: tack.codeName,
                    })
                  }
                >
                  {name}
                  {item.artworkCount['24h'] > 0 && (
                    <span className="text-primary/40">
                      {formatCount(item.artworkCount['24h'])}
                    </span>
                  )}
                </a>
              )
            })}
            <a
              href="/tags"
              className="inline-flex items-center px-2 py-0.5 rounded-md text-xs text-primary/50 hover:text-primary transition-colors"
            >
              <Text i18nKey="common:search-dropdown.see-more" />
            </a>
          </div>
        </section>
      )}

      {featured && featured.length > 0 && (
        <section>
          <h3 className="flex items-center text-sm font-semibold text-primary/60 uppercase tracking-wider mb-2">
            <Text i18nKey="common:search-dropdown.featured" />
          </h3>
          <ul>
            {featured.map(item => (
              <li key={item.title}>
                <a
                  href={item.href}
                  target={isExternalUrl(item.href) ? '_blank' : undefined}
                  rel={
                    isExternalUrl(item.href) ? 'noopener noreferrer' : undefined
                  }
                  className="flex items-center gap-2 w-full p-1 rounded-lg hover:bg-neutral-200/50 dark:hover:bg-neutral-600/50 transition-colors text-left"
                  onClick={() =>
                    track('search_dropdown_featured_click', {
                      title: item.title,
                      href: item.href,
                    })
                  }
                >
                  {item.cover ? (
                    <img
                      src={item.cover}
                      alt={item.title}
                      className="size-12 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="size-12 rounded-lg bg-neutral-200 dark:bg-neutral-600 flex items-center justify-center">
                      <IconParkNewspaperFolding className="size-5 opacity-50" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-primary text-sm font-medium truncate line-clamp-1">
                      {item.title}
                    </div>
                    <div className="text-primary/50 text-xs line-clamp-2">
                      {item.description}
                    </div>
                  </div>
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}

function SearchBarAutocompleteList({
  keyword,
  onFillback,
  scene,
}: {
  keyword: string
  onFillback: (tag: NormalizedTagItem) => void
  scene: string
}) {
  const { tags, isLoading } = useTackAutocomplete(keyword, { limit: 10 })
  const localePath = useLocalePath()

  if (isLoading && tags.length === 0) {
    return (
      <div className="text-primary/50 text-sm text-center py-6">
        <Text i18nKey="common:search-dropdown.loading" />
      </div>
    )
  }

  if (tags.length === 0) {
    return (
      <div className="text-primary/50 text-sm text-center py-6">
        <Text i18nKey="common:search-dropdown.no-results" />
      </div>
    )
  }

  return (
    <ul className="flex flex-col py-2">
      {tags.map(tag => {
        const recommendType = tag.extra.recommendType
        const codeName = tag.codeName ?? tag.name
        const href = createTagPageUrl(
          recommendType === 'market' ? 'model' : 'artwork',
          codeName,
        )
        const showCodeName = codeName !== tag.displayName
        return (
          <li key={tag.id}>
            <div className="group flex items-center w-full hover:bg-neutral-200/50 dark:hover:bg-neutral-600/50 transition-colors">
              <Link
                to={localePath(href)}
                className="flex-1 min-w-0 px-3 py-2"
                onClick={() =>
                  track('search_dropdown_autocomplete_click', {
                    scene,
                    keyword,
                    codeName,
                  })
                }
              >
                <div className="text-primary text-sm truncate">
                  {tag.displayName}
                </div>
                {showCodeName && (
                  <div className="text-primary/50 text-xs truncate">
                    {codeName}
                  </div>
                )}
              </Link>
              <button
                type="button"
                onClick={e => {
                  e.preventDefault()
                  e.stopPropagation()
                  onFillback(tag)
                }}
                className="p-2 mr-1 rounded-md opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100 hover:bg-neutral-300/50 dark:hover:bg-white/10 transition-all"
                aria-label="Fill keyword"
              >
                <IcBaselineNorthWest className="size-4 text-primary/50" />
              </button>
            </div>
          </li>
        )
      })}
    </ul>
  )
}

type DropdownBodyProps = {
  history: string[]
  onRemoveHistory: (query: string) => void
  onClearHistory: () => void
  onHistoryClick: (query: string) => void
}

function SearchHistorySection({
  history,
  onClearHistory,
  onHistoryClick,
  onRemoveHistory,
}: DropdownBodyProps) {
  if (history.length > 0) {
    return (
      <section>
        <h3 className="flex items-center gap-2 text-sm font-semibold text-primary/60 uppercase tracking-wider mb-2">
          <Text i18nKey="common:search-dropdown.recent-history" />
          <button
            className="ml-auto text-xs normal-case font-normal hover:text-primary transition-colors"
            onClick={onClearHistory}
            aria-label="Clear search history"
          >
            <Text i18nKey="common:search-dropdown.clear" />
          </button>
        </h3>
        <ul className="flex flex-col gap-1">
          {history.map(item => (
            <li key={item}>
              <div className="group flex items-center w-full rounded-lg hover:bg-neutral-200/50 dark:hover:bg-neutral-600/50 transition-colors">
                <button
                  className="flex-1 px-2 py-2 text-left text-sm"
                  onClick={() => onHistoryClick(item)}
                >
                  <span className="text-primary text-sm">{item}</span>
                </button>
                <button
                  className="p-1.5 mr-1 rounded-md opacity-0 group-hover:opacity-100 hover:bg-neutral-300/50 dark:hover:bg-white/10 transition-all"
                  onClick={e => {
                    e.stopPropagation()
                    onRemoveHistory(item)
                  }}
                  aria-label="Remove from history"
                >
                  <IcBaselineClose className="size-4 text-primary/50" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      </section>
    )
  }

  return (
    <div className="text-primary/50 text-sm text-center py-4">
      <Text i18nKey="common:search-dropdown.no-history" />
    </div>
  )
}
